import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Alert, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ListLayout from '../../Generic/ListLayout';
import DataTable from '../../Generic/DataTable';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';

const List = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const localizedTextsMap = useLocalizedTextsMap();
    const { enqueueSnackbar } = useSnackbar();

    useSetMenu(mainStore.UserApprovalMenuType, mainStore.UserApprovalMenuNo);

    const [columns, setColumns] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending"); // 'pending', 'approved', 'rejected', 'all'

    // Rejection dialog state
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [processingAction, setProcessingAction] = useState(false);

    // Fetch users based on filter
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = filter === 'pending'
                ? '/UserApproval/PendingUsers'
                : '/UserApproval/AllUsersWithStatus';
            const response = await axiosPrivate.get(endpoint);
            let data = response.data;

            // Apply filter if not 'pending' endpoint
            if (filter !== 'pending' && filter !== 'all') {
                data = data.filter(u => u.approvalStatus === filter.charAt(0).toUpperCase() + filter.slice(1));
            }

            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, filter, enqueueSnackbar, t]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Filter data based on search query
    const filteredData = useMemo(() => {
        if (!searchQuery) return users;
        const query = searchQuery.toLowerCase();
        return users.filter(user =>
            user.username?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query) ||
            user.fullName?.toLowerCase().includes(query)
        );
    }, [users, searchQuery]);

    // Approve user
    const handleApprove = async (userId) => {
        setProcessingAction(true);
        try {
            await axiosPrivate.post('/UserApproval/ApproveUser', {
                userId: userId,
                approve: true
            });
            enqueueSnackbar(t('userApprovalApproveSuccess'), { variant: 'success' });
            fetchUsers();
        } catch (error) {
            console.error('Error approving user:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Open reject dialog
    const openRejectDialog = (user) => {
        setSelectedUser(user);
        setRejectionReason("");
        setRejectDialogOpen(true);
    };

    // Close reject dialog
    const closeRejectDialog = () => {
        setRejectDialogOpen(false);
        setSelectedUser(null);
        setRejectionReason("");
    };

    // Reject user
    const handleReject = async () => {
        if (!selectedUser) return;

        setProcessingAction(true);
        try {
            await axiosPrivate.post('/UserApproval/ApproveUser', {
                userId: selectedUser.id,
                approve: false,
                rejectionReason: rejectionReason
            });
            enqueueSnackbar(t('userApprovalRejectSuccess'), { variant: 'success' });
            closeRejectDialog();
            fetchUsers();
        } catch (error) {
            console.error('Error rejecting user:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Status chip renderer
    const renderStatusChip = (status) => {
        const statusConfig = {
            'Pending': { color: 'warning', label: t('userApprovalStatusPending') },
            'Approved': { color: 'success', label: t('userApprovalStatusApproved') },
            'Rejected': { color: 'error', label: t('userApprovalStatusRejected') }
        };
        const config = statusConfig[status] || { color: 'default', label: status };
        return <Chip size="small" color={config.color} label={config.label} />;
    };

    // Columns definition
    const cols = useMemo(() => [
        {
            field: 'fullName',
            headerName: t('userFullName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'username',
            headerName: t('userUsername'),
            flex: 1,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'email',
            headerName: t('userEmail'),
            flex: 1,
            minWidth: 180,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'registrationDate',
            headerName: t('userApprovalRegistrationDate'),
            flex: 0.8,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    const date = new Date(params);
                    return format(date, "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'approvalStatus',
            headerName: t('userApprovalStatus'),
            flex: 0.6,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderStatusChip(params.value),
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                if (params.row.approvalStatus !== 'Pending') {
                    return <span className="text-muted">-</span>;
                }
                return (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApprove(params.row.id)}
                            disabled={processingAction}
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {t('userApprovalApprove')}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => openRejectDialog(params.row)}
                            disabled={processingAction}
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {t('userApprovalReject')}
                        </Button>
                    </div>
                );
            },
        },
    ], [t, processingAction]);

    useEffect(() => {
        setColumns(cols);
    }, [t, cols]);

    // Filter tabs
    const FilterTabs = () => (
        <div className="mb-3">
            <div className="btn-group" role="group">
                <button
                    type="button"
                    className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setFilter('pending')}
                >
                    {t('userApprovalStatusPending')}
                </button>
                <button
                    type="button"
                    className={`btn ${filter === 'approved' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setFilter('approved')}
                >
                    {t('userApprovalStatusApproved')}
                </button>
                <button
                    type="button"
                    className={`btn ${filter === 'rejected' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilter('rejected')}
                >
                    {t('userApprovalStatusRejected')}
                </button>
                <button
                    type="button"
                    className={`btn ${filter === 'all' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setFilter('all')}
                >
                    {t('all')}
                </button>
            </div>
        </div>
    );

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{t("userApprovalListTitle")}</h4>
            </div>

            <FilterTabs />

            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    placeholder={t("searchPlaceholder")}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ maxWidth: '300px' }}
                />
            </div>

            {loading ? (
                <div className="d-flex justify-content-center p-5">
                    <CircularProgress />
                </div>
            ) : (
                <DataTable
                    rows={filteredData}
                    columns={columns}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    localeTextsMap={localizedTextsMap}
                />
            )}

            {/* Rejection Dialog */}
            <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('userApprovalRejectTitle')}</DialogTitle>
                <DialogContent>
                    <p className="mb-3">
                        {t('userApprovalRejectConfirm', {
                            name: selectedUser?.fullName || selectedUser?.username
                        })}
                    </p>
                    <TextField
                        label={t('userApprovalRejectionReason')}
                        multiline
                        rows={3}
                        fullWidth
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder={t('userApprovalRejectionReasonPlaceholder')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRejectDialog} disabled={processingAction}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleReject}
                        color="error"
                        variant="contained"
                        disabled={processingAction}
                    >
                        {processingAction ? <CircularProgress size={24} /> : t('userApprovalReject')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default observer(List);
